import React from 'react';
import { Info, Truck, UserCheck, Warehouse } from 'lucide-react';
import { VettoreLogo } from './VettoreLogo';
import type { CondizioniTrasportoDati, EtichettaPorto } from '../../utils/condizioniTrasporto';

/** Porto con il tooltip nativo (`title`, come `RibassoBadge`) che spiega chi paga. */
function Porto({ porto }: { porto: EtichettaPorto }) {
  if (!porto.tooltip) return <>{porto.etichetta}</>;
  return (
    <span title={porto.tooltip} className="inline-flex items-center gap-1 cursor-help" data-testid="porto-etichetta">
      <span className="underline decoration-dotted underline-offset-2">{porto.etichetta}</span>
      <Info className="w-3.5 h-3.5 text-muted-foreground flex-none" aria-hidden />
    </span>
  );
}

/**
 * Righe Porto, Trasporto a cura del e Vettore (`utils/condizioniTrasporto.ts`
 * decide quali): nel riquadro "Condizioni commerciali" della Panoramica
 * (`variante="riquadro"`, con le icone) e nel dettaglio di un documento
 * (`variante="voci"`, etichetta e valore sulla stessa riga, il vettore con il logo).
 */
export function CondizioniTrasporto({ dati, variante }: {
  dati: CondizioniTrasportoDati;
  variante: 'riquadro' | 'voci';
}) {
  const righe: { chiave: string; label: string; icona: React.ElementType; valore: React.ReactNode }[] = [];
  if (dati.porto) righe.push({ chiave: 'porto', label: 'Porto', icona: Warehouse, valore: <Porto porto={dati.porto} /> });
  if (dati.trasportoACuraDel) {
    righe.push({ chiave: 'a-cura-del', label: 'Trasporto a cura del', icona: UserCheck, valore: dati.trasportoACuraDel });
  }
  if (dati.vettore) {
    righe.push({
      chiave: 'vettore', label: 'Vettore', icona: Truck,
      valore: variante === 'voci' ? <VettoreLogo nome={dati.vettore} className="h-4" /> : dati.vettore,
    });
  }

  if (variante === 'voci') {
    return (
      <>
        {righe.map(r => (
          <div key={r.chiave} className="flex justify-between gap-4 text-sm py-1" data-testid={`trasporto-${r.chiave}`}>
            <span className="text-muted-foreground">{r.label}</span>
            <span className="text-right font-medium">{r.valore}</span>
          </div>
        ))}
      </>
    );
  }
  return (
    <>
      {righe.map(({ chiave, label, icona: Icona, valore }) => (
        <div key={chiave} className="flex items-center gap-2.5" data-testid={`trasporto-${chiave}`}>
          <Icona className="w-4 h-4 text-[hsl(210,64%,29%)] flex-none" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="font-medium">{valore}</div>
          </div>
        </div>
      ))}
    </>
  );
}
